import { useEffect, useState } from 'react';
import { ArrowSquareOut, Copy, Eye, Key, Lock, ShieldCheck } from '@phosphor-icons/react';
import type { MappedApiError } from '@mybusinessmate/api-client';
import { REAUTH_REQUIRED_CODE, REVEAL_SECONDS, type RevealResult, type VaultLogin } from '@mybusinessmate/schema';
import { useRevealLogin } from '../../lib/deliverables-api';
import { countdownText, vaultIcon } from '../../lib/vault-display';
import { Icon } from '../Icon';
import { useReauth, type ReauthCopy } from '../settings/reauth';
import { EmptyState } from '../ui/empty-state';
import { useToast } from '../ui/toast';
import { toasts } from '../../lib/toast-presets';

/** The prompt for one login, in the client's words (Figma C03.4a): says what it is for, how long it shows, that it is recorded. */
const promptFor = (login: VaultLogin): ReauthCopy => ({
  title: `Show your ${login.title} login?`,
  description: `Enter your password to see it. It hides again after ${REVEAL_SECONDS} seconds, and we record every view so your team knows who opened it.`,
  confirmLabel: 'Show login',
});

/** Codes the server answers with when the stored login itself cannot be opened (not the person's doing). */
const UNOPENABLE_CODES = ['VAULT_DECRYPT_FAILED', 'VAULT_NOT_CONFIGURED'];

const TILE =
  'flex size-(--mbm-size-icon-tile-project) shrink-0 items-center justify-center rounded-(--mbm-component-radius-icon-tile) text-text-primary';

/** One value of an open login, with the button that copies it (Figma C03.4 "Username" / "Password"). */
function CopyField({ label, value }: { label: string; value: string }) {
  const toast = useToast();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast({ title: 'Copied', tone: 'success' });
    } catch {
      toast({ title: 'Couldn’t copy', description: 'Select the text and copy it.', tone: 'error' });
    }
  };
  return (
    <div className="flex min-w-0 items-center gap-(--mbm-space-2) rounded-(--mbm-component-radius-option-tile) bg-surface-card p-(--mbm-space-3)">
      <div className="flex min-w-0 flex-1 flex-col gap-(--mbm-space-0-5)">
        <span className="text-body-xsmall text-text-tertiary">{label}</span>
        <span className="select-all break-all text-body-small text-text-primary">{value}</span>
      </div>
      <button
        type="button"
        aria-label={`Copy ${label.toLowerCase()}`}
        onClick={() => void copy()}
        className="shrink-0 text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
      >
        <Icon icon={Copy} size="md" />
      </button>
    </div>
  );
}

/** A login that is open: its values, how long until it hides itself, and the way to hide it now. */
function OpenLogin({
  login,
  result,
  secondsLeft,
  onHide,
}: {
  login: VaultLogin;
  result: RevealResult;
  secondsLeft: number;
  onHide: () => void;
}) {
  const values = result.fields.filter((f) => f.type !== 'link');
  const links = result.fields.filter((f) => f.type === 'link');
  return (
    <div className="flex flex-col gap-(--mbm-space-3-5) rounded-(--mbm-component-radius-revealed-login) border border-border-revealed bg-surface-revealed p-(--mbm-space-4)">
      <div className="flex items-center gap-(--mbm-space-3)">
        <span className={`${TILE} bg-surface-card`}>
          <Icon icon={vaultIcon(login, 'login')} size="lg" />
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-(--mbm-space-0-5)">
          <span className="truncate text-label-large text-text-primary">{login.title}</span>
          {login.description ? (
            <span className="truncate text-body-xsmall text-text-tertiary">{login.description}</span>
          ) : null}
        </span>
        <span
          role="timer"
          aria-label={`Hides in ${countdownText(secondsLeft)}`}
          className="inline-flex h-(--mbm-space-6) shrink-0 items-center rounded-full bg-feedback-warning-bg px-(--mbm-space-2-5) text-label-tag text-feedback-warning"
        >
          Hides in {countdownText(secondsLeft)}
        </span>
      </div>
      <div className="grid gap-(--mbm-space-3) sm:grid-cols-2">
        {values.map((f) => (
          <CopyField key={f.id} label={f.label} value={f.value} />
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-(--mbm-space-4)">
        {links.map((f) => (
          <a
            key={f.id}
            href={/^https?:\/\//i.test(f.value) ? f.value : `https://${f.value}`}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-(--mbm-space-1-5) rounded-(--mbm-radius-xs) text-label-medium text-brand-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
          >
            <Icon icon={ArrowSquareOut} size="sm" />
            {f.label}
          </a>
        ))}
        <button
          type="button"
          onClick={onHide}
          className="inline-flex items-center gap-(--mbm-space-1-5) rounded-(--mbm-radius-xs) text-label-medium text-text-secondary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
        >
          <Icon icon={Eye} size="sm" />
          Hide now
        </button>
      </div>
    </div>
  );
}

/** The line every vault card carries: what needs the password, how long a login shows, that every view is recorded. */
function VaultNote() {
  return (
    <span className="ml-auto flex items-center gap-(--mbm-space-1-5) text-caption text-text-tertiary">
      <Icon icon={ShieldCheck} size="sm" />
      Needs your password · hides after {REVEAL_SECONDS} s · every view is recorded
    </span>
  );
}

/** A vault with nothing in it yet (Figma C03.4c): the card keeps its header, and says how things arrive. */
export function EmptyVault() {
  return (
    <section
      aria-labelledby="vault-title"
      className="flex flex-col gap-(--mbm-space-1-5) rounded-(--mbm-component-radius-widget) bg-surface-card p-(--mbm-space-4) shadow-(--mbm-shadow-table-card)"
    >
      <div className="flex flex-wrap items-center gap-x-(--mbm-space-2-5) gap-y-(--mbm-space-1) p-(--mbm-space-2)">
        <h2 id="vault-title" className="text-heading-h3 text-text-primary">
          Your vault <span className="text-text-tertiary">0</span>
        </h2>
        <VaultNote />
      </div>
      <EmptyState
        icon={Key}
        title="Nothing shared yet"
        description="When your team hands over logins, files or links (like your website admin or logo pack), they’ll show up here."
        className="py-(--mbm-space-9)"
      />
    </section>
  );
}

/**
 * The Logins card (Figma C03.4). A login stays closed until the person asks; opening one asks for their password
 * again (the server decides when that is needed), shows its values for {REVEAL_SECONDS} seconds and hides them. Only one
 * is open at a time, and the values live in this component's state only.
 */
export function LoginsCard({ logins }: { logins: VaultLogin[] }) {
  const toast = useToast();
  const reveal = useRevealLogin();
  const { guard, dialog } = useReauth({ forgotPasswordTo: '/subdomain/app/forgot-password' });
  const [open, setOpen] = useState<{ id: string; result: RevealResult; until: number } | null>(null);
  const [busyId, setBusyId] = useState<string>();
  // Logins the server could not open this visit: shown as such, with the way to tell the team, until the page reloads.
  const [unopenable, setUnopenable] = useState<ReadonlySet<string>>(new Set());
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!open) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [open]);
  useEffect(() => {
    if (open && now >= open.until) setOpen(null);
  }, [now, open]);

  const show = (login: VaultLogin, always = false) =>
    guard(
      'show this login',
      () => {
        const id = login.id;
        setBusyId(id);
        reveal.mutate(id, {
          onSuccess: (result) => {
            const start = Date.now();
            setNow(start);
            setOpen({ id, result, until: start + result.hideAfterSeconds * 1000 });
          },
          onError: (err) => {
            const e = err as Partial<MappedApiError>;
            // The server's window had lapsed even though this page thought it was open: ask again.
            if (e.code === REAUTH_REQUIRED_CODE) return show(login, true);
            if (e.code && UNOPENABLE_CODES.includes(e.code)) {
              setUnopenable((all) => new Set(all).add(id));
              return;
            }
            toast(
              e.status === 429
                ? toasts.rateLimited('Too many logins opened', 'You opened 10 in a minute. Try again in a moment.')
                : toasts.failed('Couldn’t show this login', e.message ?? 'Please try again.'),
            );
          },
          onSettled: () => setBusyId(undefined),
        });
      },
      { always, copy: promptFor(login) },
    );

  return (
    <section
      aria-labelledby="logins-title"
      className="flex flex-col gap-(--mbm-space-1-5) rounded-(--mbm-component-radius-widget) bg-surface-card p-(--mbm-space-4) shadow-(--mbm-shadow-table-card)"
    >
      <div className="flex flex-wrap items-center gap-x-(--mbm-space-2-5) gap-y-(--mbm-space-1) p-(--mbm-space-2)">
        <h2 id="logins-title" className="text-heading-h3 text-text-primary">
          Logins <span className="text-text-tertiary">{logins.length}</span>
        </h2>
        <VaultNote />
      </div>
      <ul className="flex flex-col gap-(--mbm-space-1-5)">
        {logins.map((login) => (
          <li key={login.id}>
            {open?.id === login.id ? (
              <OpenLogin
                login={login}
                result={open.result}
                secondsLeft={(open.until - now) / 1000}
                onHide={() => setOpen(null)}
              />
            ) : (
              <div className="flex items-center gap-(--mbm-space-3) p-(--mbm-space-3)">
                <span className={`${TILE} bg-surface-tint`}>
                  <Icon icon={vaultIcon(login, 'login')} size="lg" />
                </span>
                <span className="flex min-w-0 flex-1 flex-col gap-(--mbm-space-0-5)">
                  <span className="truncate text-label-large text-text-primary">{login.title}</span>
                  {unopenable.has(login.id) ? (
                    <span role="alert" className="text-body-xsmall text-feedback-warning">
                      This login can’t be opened. Ask your team to add it again.
                    </span>
                  ) : login.description ? (
                    <span className="truncate text-body-xsmall text-text-tertiary">{login.description}</span>
                  ) : null}
                </span>
                <button
                  type="button"
                  disabled={busyId === login.id}
                  aria-label={unopenable.has(login.id) ? `Ask your team about ${login.title}` : `Reveal ${login.title}`}
                  onClick={() =>
                    unopenable.has(login.id)
                      ? toast(toasts.comingSoon('Messaging your team arrives with chat.'))
                      : show(login)
                  }
                  className="inline-flex h-(--mbm-size-toolbar-control) shrink-0 items-center gap-(--mbm-space-1-5) rounded-full border border-border-default bg-surface-card px-(--mbm-space-3-5) text-label-medium text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring) disabled:opacity-(--mbm-opacity-pressed)"
                >
                  <Icon icon={Lock} size="xs" />
                  {unopenable.has(login.id) ? 'Ask team' : 'Reveal'}
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
      {dialog}
    </section>
  );
}
